<template>
  <SDialog :open="confirmDialogState.open" @update:open="onOpenChange">
    <SDialogContent
      class="max-w-[440px] sm:max-w-[440px]"
      :show-close-button="false"
      data-test="confirm-dialog"
      @escape-key-down="onCancel"
      @pointer-down-outside="onCancel"
      @open-auto-focus.prevent
    >
      <SDialogHeader>
        <SDialogTitle
          class="flex items-center gap-2.5 text-lg font-semibold leading-tight"
        >
          <i
            v-if="getVariant === 'warning'"
            class="s-icon s-icon--triangle-alert text-amber-500"
            style="--s-icon-size: 1.25rem"
            aria-hidden="true"
          />
          <span>{{ confirmDialogState.options.title }}</span>
        </SDialogTitle>
      </SDialogHeader>

      <div
        class="text-sm text-muted-foreground leading-relaxed"
        data-test="confirm-dialog-body"
      >
        <component
          :is="confirmDialogState.options.body"
          v-if="confirmDialogState.options.body"
        />
        <p
          v-else-if="confirmDialogState.options.message"
          class="whitespace-pre-line"
        >
          <template v-if="getHighlightParts">
            {{ getHighlightParts[0]
            }}<span class="font-semibold text-foreground">{{
              confirmDialogState.options.highlight
            }}</span
            >{{ getHighlightParts[1] }}
          </template>
          <template v-else>
            {{ confirmDialogState.options.message }}
          </template>
        </p>
      </div>

      <SDialogFooter class="sm:justify-center gap-3">
        <SButton
          v-if="getShowCancel"
          variant="ghost"
          size="lg"
          class="min-w-[140px]"
          data-test="confirm-dialog-cancel"
          @click="onCancel"
        >
          {{ getCancelLabel }}
        </SButton>
        <SButton
          :variant="getConfirmVariant"
          size="lg"
          class="min-w-[140px]"
          data-test="confirm-dialog-confirm"
          @click="onConfirm"
        >
          {{ getConfirmLabel }}
        </SButton>
      </SDialogFooter>
    </SDialogContent>
  </SDialog>
</template>

<script setup lang="ts">
/**
 * @component ConfirmDialog
 * Singleton confirmation dialog driven by the `showConfirm()` helper.
 *
 * Mount once at the app root (App.vue). The component reads its state from
 * `confirmDialogState` and resolves the in-flight promise via `resolveConfirm()`
 * on button click, Esc, or click-outside.
 *
 * Three variants:
 *   - `default`     primary teal confirm, no icon (low-stakes confirmations).
 *   - `warning`     amber triangle-alert icon, primary teal confirm.
 *   - `destructive` no icon, red confirm button (irreversible actions).
 *
 * @example
 *   // In App.vue
 *   <ConfirmDialog />
 *
 *   // Anywhere else
 *   const ok = await showConfirm({ title: "...", message: "...", variant: "destructive" });
 */
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogFooter,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";
import "@smeetz/ui-icons/triangle-alert/style.css";
import {
  confirmDialogState,
  resolveConfirm,
} from "@/utils/vue-helpers/confirm-dialog";

const { t } = useI18n();

const getVariant = computed(
  () => confirmDialogState.options.variant ?? "default"
);

const getConfirmVariant = computed(() =>
  getVariant.value === "destructive" ? "destructive" : "default"
);

const getShowCancel = computed(
  () => confirmDialogState.options.cancelText !== null
);

const getConfirmLabel = computed(
  () => confirmDialogState.options.confirmText ?? t("button.confirm")
);

const getCancelLabel = computed(
  () => confirmDialogState.options.cancelText ?? t("button.cancel")
);

/**
 * Split message at the literal `XXXX` placeholder when `highlight` is set.
 * Returns the two surrounding fragments, or `null` if the message can't
 * be highlighted (no placeholder, no highlight value, or no message).
 */
const getHighlightParts = computed<[string, string] | null>(() => {
  const { message, highlight } = confirmDialogState.options;
  if (!message || !highlight) {
    return null;
  }
  const parts = message.split("XXXX");
  if (parts.length !== 2) {
    return null;
  }
  return [parts[0], parts[1]];
});

/**
 * Resolve the dialog as confirmed.
 *
 * @returns {void}
 */
function onConfirm(): void {
  resolveConfirm(true);
}

/**
 * Resolve the dialog as cancelled.
 *
 * @returns {void}
 */
function onCancel(): void {
  resolveConfirm(false);
}

/**
 * Forward `update:open` from SDialog to the store.
 * @param {boolean} open - Next open state from SDialog.
 * @returns {void}
 */
function onOpenChange(open: boolean): void {
  if (!open) {
    resolveConfirm(false);
  }
}
</script>
